import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, withCtx as _withCtx, toDisplayString as _toDisplayString, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, withKeys as _withKeys, withModifiers as _withModifiers, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-3501e444"),n=n(),_popScopeId(),n)
const _hoisted_1 = {
  key: 0,
  class: "flex"
}
const _hoisted_2 = { class: "ion-padding" }

import { IonButton, IonContent, IonFab, IonFabButton, IonIcon, IonInput, IonItem, IonPage, IonSpinner, loadingController, onIonViewWillEnter } from '@ionic/vue';
import { ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { arrowForwardOutline, gridOutline } from 'ionicons/icons';
import { hasError } from '@hotwax/oms-api';
import { useAuthStore } from '@/store/auth';
import Logo from '@/components/Logo.vue';
import { UserService } from '@/services/UserService';
import { translate } from '@/i18n';
import { appInfo, showToast } from '@/util';
import { Actions, hasPermission } from '@/authorization';


export default /*#__PURE__*/_defineComponent({
  __name: 'Login',
  setup(__props) {

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();

const username = ref('');
const password = ref('');
const instanceUrl = ref('');
const baseURL = process.env.VUE_APP_BASE_URL;
const alias = process.env.VUE_APP_ALIAS ? JSON.parse(process.env.VUE_APP_ALIAS) : {};
const defaultAlias = process.env.VUE_APP_DEFAULT_ALIAS;
const hideBackground = ref(true);
const loader = ref<any>(null);
const loginOption = ref<any>({});
const isLoggingIn = ref(false);

const presentLoader = async (message: string) => {
  if (!loader.value) {
    loader.value = await loadingController.create({
      message: translate(message),
      translucent: true,
      backdropDismiss: false
    });
  }
  loader.value.present();
};

const dismissLoader = () => {
  if (loader.value) {
    loader.value.dismiss();
    loader.value = null as any;
  }
};

// on pressing Enter after inputting OMS, the form is submitted through the login method
// handleSubmit will handle the flow based on the input values for OMS, username and password
const handleSubmit = () => {
  if (instanceUrl.value) login();
};

const fetchLoginOptions = async () => {
  loginOption.value = {};
  try {
    const resp = await UserService.checkLoginOptions();
    if (!hasError(resp)) {
      loginOption.value = resp.data;
    }
  } catch (error) {
    console.error(error);
  }
};

const setOms = async () => {
  if (!instanceUrl.value) {
    showToast(translate('Please fill in the OMS'));
    return;
  }

  const instanceURL = instanceUrl.value.trim().toLowerCase();
  if (!baseURL) authStore.setOMS(alias[instanceURL] ? alias[instanceURL] : instanceURL);

  // run SAML login flow if login options are configured for the OMS
  await fetchLoginOptions();

  if (Object.keys(loginOption.value).length && loginOption.value.loginAuthType !== 'BASIC') {
    window.location.href = `${loginOption.value.loginAuthUrl}?relaystate=${window.location.origin}/login`; // passing launchpad/login URL
  }
};

const generateRedirectionLink = () => {
  let url = authStore.getRedirectUrl;
  const app = appInfo.find((app: any) => url.includes(app.handle))!;

  // Replacing legacy from the url, so to easily handle the redirection
  url = url.replaceAll('-legacy', '');

  if ((app && app.appLegacyPermission && Actions[app.appLegacyPermission] && hasPermission(Actions[app.appLegacyPermission])) || (app && app.appPermission && Actions[app.appPermission] && !hasPermission(Actions[app.appPermission]))) {
    if (url.includes('-uat.hotwax.io') || url.includes('-dev.hotwax.io')) {
      url = url.replace('-uat.hotwax.io', '-legacy-uat.hotwax.io').replace('-dev.hotwax.io', '-legacy-dev.hotwax.io');
    } else {
      url = url.replace('.hotwax.io', '-legacy.hotwax.io');
    }
  }

  const omsUrl = authStore.oms.startsWith('http') ? (authStore.oms.includes('/api') ? authStore.oms : `${authStore.oms}/api/`) : authStore.oms;
  window.location.replace(`${url}?oms=${omsUrl}&token=${authStore.token.value}&expirationTime=${authStore.token.expiration}`);
};

const login = async () => {
  if (!username.value || !password.value) {
    showToast(translate('Please fill in the user details'));
    return;
  }

  isLoggingIn.value = true;
  try {
    await authStore.login(username.value.trim(), password.value);
    if (authStore.getRedirectUrl) {
      generateRedirectionLink();
    } else {
      // All the failure cases are handled in action, if then block is executing, login is successful
      username.value = '';
      password.value = '';
      router.push('/');
    }
  } catch (error) {
    console.error(error);
  }
  isLoggingIn.value = false;
};

const samlLogin = async () => {
  try {
    const { token, expirationTime } = route.query as any;
    await authStore.samlLogin(token, expirationTime);
    if (authStore.getRedirectUrl) {
      generateRedirectionLink();
    } else {
      router.push('/');
    }
  } catch (error) {
    router.push('/');
    console.error(error);
  }
};

const basicLogin = async () => {
  try {
    const { oms, token, expirationTime } = route.query as any;
    // Clear the previously stored oms and token when having oms and token in the URL
    await authStore.setToken('', '');
    await authStore.setOMS(oms);

    // Setting token previous to getting user-profile, if not then the client method honors the state token
    await authStore.setToken(token, expirationTime);

    const current = await UserService.getUserProfile(token);
    await authStore.setCurrent(current);

    await authStore.getPermissions();
  } catch (error) {
    showToast(translate('Failed to fetch user-profile, please try again'));
    console.error('error: ', error);
  }
  router.replace('/');
};

const initialise = async () => {
  hideBackground.value = true;
  await presentLoader('Processing');

  // Run the basic login flow when oms and token both are found in query
  if (route.query?.oms && route.query?.token) {
    await basicLogin();
    dismissLoader();
    return;
  } else if (route.query?.token) {
    // SAML login handling as only token will be returned in the query when login through SAML
    await samlLogin();
    dismissLoader();
    return;
  }

  // logout from Launchpad if logged out from the app
  if (route.query?.isLoggedOut === 'true') {
    // We will already mark the user as unuauthorised when log-out from the app
    await authStore.logout({ isUserUnauthorised: true });
  }

  // fetch login options only if OMS is there as API calls require OMS
  if (authStore.getOMS) {
    await fetchLoginOptions();
  }

  // Update OMS input if found in query
  if (route.query?.oms) {
    instanceUrl.value = route.query.oms as string;
  }

  // setting redirectUrl in the state
  if (route.query?.redirectUrl) {
    authStore.setRedirectUrl(route.query.redirectUrl as string);
  }

  // if a session is already active, login directly in the app
  if (authStore.isAuthenticated) {
    if (authStore.getRedirectUrl) {
      await authStore.getPermissions();
      generateRedirectionLink();
    } else {
      router.push('/');
    }
  }

  instanceUrl.value = authStore.oms;
  if (authStore.oms) {
    // If the current URL is available in alias show it for consistency
    const currentInstanceUrlAlias = Object.keys(alias).find((key) => alias[key] === authStore.oms);
    if (currentInstanceUrlAlias) instanceUrl.value = currentInstanceUrlAlias;
  }
  // If there is no current preference set the default one
  if (!instanceUrl.value && defaultAlias) {
    instanceUrl.value = defaultAlias;
  }

  if (!instanceUrl.value) {
    instanceUrl.value = process.env.VUE_APP_OMS_URL;
    setOms();
  }

  dismissLoader();
  hideBackground.value = false;
};

onIonViewWillEnter(() => {
  // Updating state with the instance URL defined in env as we have defined instance URL hardcoded
  instanceUrl.value = process.env.VUE_APP_OMS_URL;
  initialise();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(IonPage), null, {
    default: _withCtx(() => [
      _createVNode(_unref(IonContent), null, {
        default: _withCtx(() => [
          (!hideBackground.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createElementVNode("form", {
                  class: "login-container",
                  onKeyup: _cache[3] || (_cache[3] = _withKeys(($event: any) => (handleSubmit()), ["enter"])),
                  onSubmit: _cache[4] || (_cache[4] = _withModifiers(() => {}, ["prevent"]))
                }, [
                  _createVNode(Logo),
                  _createElementVNode("section", null, [
                    _createVNode(_unref(IonItem), { lines: "full" }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(IonInput), {
                          label: _ctx.$t('Username'),
                          "label-placement": "fixed",
                          name: "username",
                          modelValue: username.value,
                          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((username).value = $event)),
                          id: "username",
                          type: "text",
                          required: ""
                        }, null, 8, ["label", "modelValue"])
                      ]),
                      _: 1
                    }),
                    _createVNode(_unref(IonItem), { lines: "none" }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(IonInput), {
                          label: _ctx.$t('Password'),
                          "label-placement": "fixed",
                          name: "password",
                          modelValue: password.value,
                          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((password).value = $event)),
                          id: "password",
                          type: "password",
                          required: ""
                        }, null, 8, ["label", "modelValue"])
                      ]),
                      _: 1
                    }),
                    _createElementVNode("div", _hoisted_2, [
                      _createVNode(_unref(IonButton), {
                        color: "primary",
                        expand: "block",
                        onClick: _cache[2] || (_cache[2] = ($event: any) => (isLoggingIn.value ? '' : login()))
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(_ctx.$t("Login")) + " ", 1),
                          (isLoggingIn.value)
                            ? (_openBlock(), _createBlock(_unref(IonSpinner), {
                                key: 0,
                                slot: "end",
                                name: "crescent"
                              }))
                            : (_openBlock(), _createBlock(_unref(IonIcon), {
                                key: 1,
                                slot: "end",
                                icon: _unref(arrowForwardOutline)
                              }, null, 8, ["icon"]))
                        ]),
                        _: 1
                      })
                    ])
                  ])
                ], 32)
              ]))
            : _createCommentVNode("", true),
          _createVNode(_unref(IonFab), {
            onClick: _cache[5] || (_cache[5] = ($event: any) => (_unref(router).push('/'))),
            vertical: "bottom",
            horizontal: "end",
            slot: "fixed"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(IonFabButton), { color: "medium" }, {
                default: _withCtx(() => [
                  _createVNode(_unref(IonIcon), { icon: _unref(gridOutline) }, null, 8, ["icon"])
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    _: 1
  }))
}
}

})